import { publicApi } from "@/services/publicApi";
import type {
  PhoneLookupResult,
  SlotReservation,
  ReserveSlotForZipCodeResult,
  ServiceAreaResult,
  CreateBookingResult,
  CreatePublicBookingRequest,
  CancelBookingResult,
  BookingDetails,
  ManageLinkLookupRequest,
  ManageLinkCancelRequest,
  CreateBookingApprovalRequest,
  RequestBookingApprovalResult,
  BookingApprovalMode,
  BookingApprovalStatus,
  UpcomingHouseholdCheckRequest,
  UpcomingHouseholdCheckResult,
  ProgramCheckRequest,
  ProgramCheckResult,
  IncomeGuidelines,
  IncomeGuidelinesRequest
} from "./publicBookingFlowTypes";

export const publicBookingApi = publicApi.injectEndpoints({
  endpoints: (builder) => ({
    // Server-side ports the full zip → site → slot search (see PublicBookingReservationService) and
    // reserves the earliest open slot at the first servicing site with availability.
    reserveSlotForZipCode: builder.mutation<ReserveSlotForZipCodeResult, string>({
      query: (zipCode) => ({
        url: "/api/ams/slots/reservations",
        method: "POST",
        body: { zipCode },
        // AmsSlotsController.ReserveSlot uses these statuses to signal outcome, not just success/failure.
        // 502 is included deliberately: it is the "Cal could not be read" answer, which is a distinct
        // outcome from "nothing free" and must not surface as a generic transport error.
        validateStatus: (response) => [200, 204, 404, 502].includes(response.status),
      }),
      transformResponse: (response: SlotReservation | ServiceAreaResult | undefined, meta) => {
        if (meta?.response?.status === 204) return { status: "noSlotsAvailable" as const };
        if (meta?.response?.status === 502) return { status: "unavailable" as const };
        if (meta?.response?.status === 404) return { status: "outOfServiceArea" as const, serviceArea: response as ServiceAreaResult };
        return { status: "reserved" as const, slotReservation: response as SlotReservation };
      },
    }),
    releaseSlotReservation: builder.mutation<void, string>({
      query: (reservationUid) => ({
        url: `/api/ams/slots/reservations/${reservationUid}`,
        method: "DELETE",
      }),
    }),
    // A mutation, not a query: this POSTs to a metered Twilio lookup and must not be served from
    // cache when the same number is submitted twice.
    verifyPhoneNumber: builder.mutation<PhoneLookupResult, string>({
      query: (phone) => ({
        url: "/api/phone/verify",
        method: "POST",
        body: { phoneNumber: phone }
      })
    }),
    createBooking: builder.mutation<CreateBookingResult, CreatePublicBookingRequest>({
      query: (payload) => ({
        url: "/api/ams/bookings",
        method: "POST",
        body: payload
      })
    }),
    // The monthly income limits to show for the household size just entered. A query, not a
    // mutation: it is a plain GET keyed on a household size and the energy answers, carries nothing
    // identifying, and the same answers asked twice should come from cache — the opposite of the
    // SSN-bearing calls below. Programs already received (AB#595) are read server-side from the
    // session's step-2 program check, never sent from here.
    getIncomeGuidelines: builder.query<IncomeGuidelines, IncomeGuidelinesRequest>({
      query: ({ householdSize, energySource, pseService }) => {
        const params = new URLSearchParams({ householdSize: String(householdSize) });
        if (energySource) params.set("energySource", energySource);
        if (energySource === "PSE" && pseService) params.set("pseService", pseService);
        return `/api/ams/income-guidelines?${params.toString()}`;
      }
    }),
    // Does this Social Security number already have an upcoming appointment against it? A mutation
    // rather than a query: it carries a Social Security number, and a cached query keyed on one would
    // keep it in the RTK Query store for the life of the tab.
    checkUpcomingHouseholdBooking: builder.mutation<UpcomingHouseholdCheckResult, UpcomingHouseholdCheckRequest>({
      query: (payload) => ({
        url: "/api/ams/bookings/upcoming-household-check",
        method: "POST",
        body: payload
      })
    }),
    // Has the household behind this Social Security number any program left to book this program year
    // (AB#590)? A mutation for the same reason as the check above: it carries a Social Security number.
    checkPrograms: builder.mutation<ProgramCheckResult, ProgramCheckRequest>({
      query: (payload) => ({
        url: "/api/ams/bookings/program-check",
        method: "POST",
        body: payload
      })
    }),
    // Forgets this session's stored program-check answer (QA, AB#590/595). Sent when the check above
    // fails for any reason, a 429 included, so the guidelines step never withholds on an earlier
    // number's answer. No body: it only ever clears the caller's own session.
    clearProgramCheck: builder.mutation<void, void>({
      query: () => ({
        url: "/api/ams/bookings/program-check",
        method: "DELETE"
      })
    }),
    // Asks us to ring the number on that appointment and read it out. Takes the opaque notice id from
    // the check above and nothing else — the page never holds the number, only its last four digits,
    // and cannot name an appointment its own session did not resolve.
    callUpcomingBookingDetails: builder.mutation<void, string>({
      query: (noticeId) => ({
        url: `/api/ams/bookings/upcoming-household-check/${noticeId}/call`,
        method: "POST"
      })
    }),
    // The standalone phone-number + confirmation-code lookup and cancel endpoints are gone: a
    // booking session is free to mint, so those two were a brute-force surface against a 6-digit
    // code aimed at a number the caller already knew. What replaced them is createBookingApproval
    // below — which still serves that two-factor check, but only while the server's phone challenge
    // is switched off, and behind the same attempt cap. The manage-link pair further down is the
    // other way in.
    // Asks for a view or a cancellation, and gets back whichever authorization the server is
    // configured to require (see RequestBookingApprovalResult). With the phone challenge on it
    // answers 202 whatever the code was, so an error means the request itself was refused (429 for
    // the attempt cap) and never that the booking was not found; with it off the work is already
    // done on a 200, and a 404 means the phone number and code matched nothing.
    createBookingApproval: builder.mutation<RequestBookingApprovalResult, CreateBookingApprovalRequest>({
      query: (payload) => ({
        url: "/api/ams/booking-approvals",
        method: "POST",
        body: payload
      })
    }),
    // Which authorization the server is applying, for copy that has to be written before there is a
    // response to read. Cached like any other query — the flag it reports moves on a config change,
    // not within a visit — and deliberately not used to decide behaviour: that comes from what
    // createBookingApproval actually returns, so a flag flipped mid-visit cannot desync the page.
    getBookingApprovalMode: builder.query<BookingApprovalMode, void>({
      query: () => "/api/ams/booking-approvals/mode"
    }),
    // A query rather than a mutation, and the only polled endpoint in this slice: pollingInterval
    // exists on queries alone. The approvalId is part of the cache key, so nothing is ever served
    // from a previous approval's result.
    getBookingApproval: builder.query<BookingApprovalStatus, string>({
      query: (approvalId) => `/api/ams/booking-approvals/${approvalId}`
    }),
    // Withdraws a request the page has stopped waiting on. Not just a local "stop polling": the
    // approval goes out as a phone call and an unanswered one is re-attempted, so without telling the
    // server, someone who gave up here would be rung a second time for a request nobody is watching.
    cancelBookingApproval: builder.mutation<void, string>({
      query: (approvalId) => ({
        url: `/api/ams/booking-approvals/${approvalId}`,
        method: "DELETE"
      })
    }),
    // The cancellation-link pair. Same shape as lookupBooking/cancelBooking above, but identifying
    // the applicant by the signed link token.
    lookupBookingByLink: builder.mutation<BookingDetails, ManageLinkLookupRequest>({
      query: (payload) => ({
        url: "/api/ams/bookings/manage-link/lookup",
        method: "POST",
        body: payload
      })
    }),
    cancelBookingByLink: builder.mutation<CancelBookingResult, ManageLinkCancelRequest>({
      query: (payload) => ({
        url: "/api/ams/bookings/manage-link/cancel",
        method: "POST",
        body: payload
      })
    })
  })
});

export const {
  useReserveSlotForZipCodeMutation,
  useReleaseSlotReservationMutation,
  useVerifyPhoneNumberMutation,
  useCreateBookingMutation,
  useLookupBookingByLinkMutation,
  useCancelBookingByLinkMutation,
  useCreateBookingApprovalMutation,
  useGetBookingApprovalModeQuery,
  useGetBookingApprovalQuery,
  useCancelBookingApprovalMutation,
  useCheckUpcomingHouseholdBookingMutation,
  useCheckProgramsMutation,
  useClearProgramCheckMutation,
  useCallUpcomingBookingDetailsMutation,
  useGetIncomeGuidelinesQuery,
} = publicBookingApi;
